import {
  DownloadOutlined,
  DownOutlined,
  FileExcelOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Dropdown, MenuProps, Tabs, TabsProps } from 'antd';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import useTrackerWrapper from 'hooks/useTracker';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import { PermissionKey } from 'types/rbac';
import {
  downloadFile,
  getStakeholderTypeForCountry,
  isMobileScreenSize,
} from 'utils/helpers';
import testId from 'utils/testId';

const buttonDropdownItems = (hasFailed: boolean) => [
  {
    key: '1',
    label: 'Add Individually',
    icon: <FileExcelOutlined />,
  },
  {
    key: '2',
    label: 'Upload CSV',
    icon: <UserOutlined />,
  },
  ...(hasFailed
    ? [
        {
          key: '3',
          label: 'Download Failed Result',
          icon: <DownloadOutlined />,
        },
      ]
    : []),
];

type StakeholderTabsKeys = '1' | '2' | '3';

type StakeholderTabsProps = {
  country: string;
  tabsItems: TabsProps['items'];
  onTabChange: () => void;
  onClickUpload: () => void;
  downloadUrl: string;
  activeTab: StakeholderTabsKeys;
  setActiveTab: React.Dispatch<React.SetStateAction<StakeholderTabsKeys>>;
};

const StakeholderTabs = ({
  country,
  tabsItems,
  onTabChange,
  onClickUpload,
  downloadUrl,
  activeTab,
  setActiveTab,
}: StakeholderTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { onClickWrapper } = useTrackerWrapper();

  const handleNavigateWithTracker = (label: string, route: string) => {
    onClickWrapper(label, () => navigate(route));
  };

  const stakeholderProperties = {
    '1': {
      onTabChange: () =>
        handleNavigateWithTracker('Paddy Buyer Tab', routes.paddyBuyer),
      onButtonClick: () =>
        handleNavigateWithTracker('Add Paddy Buyer', routes.paddyBuyerCreate),
      buttonLabel: t('common.add', {
        value: getStakeholderTypeForCountry(country, 'PB'),
      }),
      pathname: routes.paddyBuyer,
      testId: testId.stakeholderPage['btn-stakeholder-add-paddy_buyer'],
      permission: 'paddy_buyer:add',
    },
    '2': {
      onTabChange: () =>
        handleNavigateWithTracker(
          'Farmer Group Leader Tab',
          routes.farmerGroupLeader,
        ),
      onButtonClick: () =>
        handleNavigateWithTracker(
          'Add Farmer Group Leader',
          routes.farmerGroupLeaderCreate,
        ),
      buttonLabel: t('stakeholders.addFarmerGroupLeader'),
      pathname: routes.farmerGroupLeader,
      testId: testId.stakeholderPage['btn-stakeholder-add-farmer_group_leader'],
      permission: 'farmer_group_leader:add',
    },
    '3': {
      onTabChange: () =>
        handleNavigateWithTracker('Machine Owner Tab', routes.machineOwner),
      onButtonClick: () =>
        handleNavigateWithTracker(
          'Add Machine Owner',
          routes.machineOwnerCreate,
        ),
      buttonLabel: t('stakeholders.addMachineOwner'),
      pathname: routes.machineOwner,
      testId: testId.stakeholderPage['btn-stakeholder-add-machine_owner'],
      permission: 'machine_owner:add',
    },
  };

  const handleButtonClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case '1':
        stakeholderProperties[activeTab].onButtonClick();
        break;
      case '2':
        if (activeTab !== '3') onClickUpload();
        break;
      case '3':
        if (activeTab !== '3')
          downloadFile(downloadUrl, downloadUrl?.split('/')?.pop() ?? '');
        break;
      default:
        break;
    }
  };

  const tabsExtraContent = (
    <ProtectedResource
      permission={stakeholderProperties[activeTab].permission as PermissionKey}
    >
      <Dropdown.Button
        type="primary"
        menu={{
          items: buttonDropdownItems(!!downloadUrl),
          onClick: handleButtonClick,
        }}
        data-testid={testId.stakeholderPage['btn-stakeholder-add-paddy_buyer']}
        onClick={stakeholderProperties[activeTab].onButtonClick}
        icon={<DownOutlined />}
        buttonsRender={([button, dropdown]) => [
          React.cloneElement(button as React.ReactElement, {
            'data-testId': stakeholderProperties[activeTab].testId,
          }),
          React.cloneElement(dropdown as React.ReactElement, {
            'data-testId':
              testId.stakeholderPage.tabs['btn-stakeholder-tab-arrow_dropdown'],
          }),
        ]}
      >
        {stakeholderProperties[activeTab].buttonLabel}
      </Dropdown.Button>
    </ProtectedResource>
  );

  useEffect(() => {
    Object.keys(stakeholderProperties).forEach((key) => {
      if (
        location.pathname ===
        stakeholderProperties[key as StakeholderTabsKeys].pathname
      ) {
        setActiveTab(key as StakeholderTabsKeys);
      }
    });
  }, [location]);

  useEffect(() => {
    onTabChange();
  }, [activeTab]);

  return (
    <>
      {isMobileScreenSize && (
        <div className="stakeholder-button-container">{tabsExtraContent}</div>
      )}
      <Tabs
        defaultActiveKey="1"
        items={tabsItems}
        tabBarExtraContent={!isMobileScreenSize && tabsExtraContent}
        onChange={(key) =>
          stakeholderProperties[key as StakeholderTabsKeys].onTabChange()
        }
        activeKey={activeTab}
      />
    </>
  );
};

export default StakeholderTabs;
